import { post } from 'aws-amplify/api';
import { PRESCRIPTSURE } from '../constants/api-endpoints';
import { getSessionToken } from './service-config';

const apiNamePath = PRESCRIPTSURE.ANALYTICS_API;

const InvestorMetricsService = {
  // ==================== RETENTION METRICS ====================
  
  async getPrescriberRetention7d({ dateRange = '30d', organization }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/retention_metrics',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'prescriber_retention_7d',
          date_range: dateRange,
          organization,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getPrescriberRetention30d({ dateRange = '90d', organization }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/retention_metrics',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'prescriber_retention_30d',
          date_range: dateRange,
          organization,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getDAUMAUStickiness({ dateRange = '30d', organization }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/retention_metrics',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'dau_mau_stickiness',
          date_range: dateRange,
          organization,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getPowerUserSegmentation({ dateRange = '30d', organization }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/retention_metrics',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'power_user_segmentation',
          date_range: dateRange,
          organization,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getPracticeRetention({ dateRange = '30d' }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/retention_metrics',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'practice_retention',
          date_range: dateRange,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  // ==================== PRACTICE METRICS ====================

  async getActivePractices({ dateRange = '30d' }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/practice_metrics',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'active_practices',
          date_range: dateRange,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getPracticeLevelRetention({ dateRange = '90d', practiceId }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/practice_metrics',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'practice_level_retention',
          date_range: dateRange,
          practice_id: practiceId,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getPracticesByUsageTier({ dateRange = '30d' }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/practice_metrics',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'practices_by_usage_tier',
          date_range: dateRange,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getPracticeEngagementTrends({ dateRange = '90d' }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/practice_metrics',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'practice_engagement_trends',
          date_range: dateRange,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  // ==================== USER ACTIVITY ====================

  async getActivePrescribers({ period = '30d' }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/user_activity',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'active_prescribers',
          period,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getCoverageSearchesPerPrescriberPerWeek({ weeks = 4 }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/user_activity',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'coverage_searches_per_prescriber_per_week',
          weeks,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getDAUMAUCalculation({ days = 30 }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/user_activity',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'dau_mau_calculation',
          days,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  // ==================== COVERAGE ACTIVITY ====================

  async getCoverageChecksByRole({ organization, dateRange = '30d' }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/coverage_activity',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'coverage_checks_by_role',
          organization,
          date_range: dateRange,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getFeatureModuleAdoption({ organization, dateRange = '30d' }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/coverage_activity',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'feature_module_adoption',
          organization,
          date_range: dateRange,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },

  async getApiCallFrequency({ organization, dateRange = '30d' }) {
    const sessionToken = await getSessionToken();

    const operation = post({
      apiName: apiNamePath,
      path: '/coverage_activity',
      options: {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        body: {
          query_path: 'api_call_frequency',
          organization,
          date_range: dateRange,
        },
      },
    });

    const result = await operation.response;
    return result.body.json();
  },
};

export default InvestorMetricsService;
